import {
  assert,
  Ref,
  RefMap,
  RefSet,
  TwinPair,
  bothOrNull,
} from 'src/util';

import App from './App';
import Room from './Room';

import Player from './Player';
import Game from './Game';

import AnimatedRatings from './AnimatedRatings';

import { Simulator } from './Simulator';

import Calculator from './Calculator';

import { VState } from 'src/ui/GameRow';
import * as GameRowDialog from 'src/ui/GameRow/Dialog';
import * as PlayerSelectorDialog from 'src/ui/PlayerSelector/Dialog';
import * as RoomSettingsDialog from 'src/ui/Room/RoomSettingsDialog';
import * as RoomSelectorDialog from 'src/ui/Room/RoomSelectorDialog';

export type RoomPage = {
  room: Ref<Room>,
  expandedSections: { [key: string]: boolean };
  selectedPlayers: TwinPair<Ref<Player> | null>;
  animatedRatings: AnimatedRatings;
  simulator?: Simulator;
  dialog: {
    hidden: boolean;
    state?: RoomPage.DialogState;
  };
  vStates: { [tag: string]: VState };
};

export function RoomPage(room: Ref<Room>): RoomPage {
  return {
    room,
    expandedSections: {
      'Add Game': true,
      'Leaderboard': true,
    },
    selectedPlayers: TwinPair(null, null),
    animatedRatings: AnimatedRatings(),
    dialog: { hidden: true },
    vStates: {},
  };
}

export namespace RoomPage {
  export type Action = (
    {
      type: 'set-partial',
      data: Partial<RoomPage>,
    } |
    {
      type: 'expand-section',
      data: string,
    } |
    {
      type: 'collapse-section',
      data: string,
    } |
    {
      type: 'toggle-section',
      data: string,
    } |
    {
      type: 'select-player',
      player: Ref<Player> | null,
      side: Player.Side,
    } |
    {
      type: 'time-elapsed',
      data: number,
    } |
    {
      type: 'set-simulator',
      data: Simulator,
    } |
    {
      type: 'set-dialog',
      data?: DialogState,
    } |
    {
      type: 'show-dialog',
    } |
    {
      type: 'hide-dialog',
    } |
    never
  );

  export function reduce(page: RoomPage, app: App, action: Action): RoomPage {
    switch (action.type) {
      case 'set-partial': {
        return { ...page, ...action.data };
      }

      case 'expand-section': {
        return { ...page,
          expandedSections: { ...page.expandedSections,
            [action.data]: true,
          },
        };
      }

      case 'collapse-section': {
        return { ...page,
          expandedSections: { ...page.expandedSections,
            [action.data]: false,
          },
        };
      }

      case 'toggle-section': {
        return { ...page,
          expandedSections: { ...page.expandedSections,
            [action.data]: !page.expandedSections[action.data],
          },
        };
      }

      case 'select-player': {
        const { player, side } = action;
        let opponent = Player.Pair.Opponent(page.selectedPlayers, side);

        if (Ref.equals(player, opponent)) {
          opponent = null;
        }

        return { ...page,
          selectedPlayers: Player.Pair(player, opponent, side),
        };
      }

      case 'time-elapsed': {
        const room = resolveRoom(page, app);

        if (room === null) {
          return page;
        }

        const targetRatings = Room.Ratings(room, app.calculator);

        if (targetRatings === null) {
          return page;
        }

        return { ...page,
          animatedRatings: AnimatedRatings.reduce(
            page.animatedRatings,
            {
              target: targetRatings,
              dt: action.data,
            },
          ),
        };
      }

      case 'set-simulator': {
        return { ...page,
          simulator: action.data,
        };
      }

      case 'set-dialog': {
        if (action.data) {
          return { ...page,
            dialog: { ...page.dialog, state: action.data },
          };
        }

        return { ...page,
          dialog: { hidden: true },
        };
      }

      case 'show-dialog': {
        return { ...page, dialog: { ...page.dialog, hidden: false } };
      }

      case 'hide-dialog': {
        return { ...page, dialog: { ...page.dialog, hidden: true } };
      }
    }
  }

  export type DialogState = (
    PlayerSelectorDialog.State |
    GameRowDialog.State |
    RoomSettingsDialog.State |
    RoomSelectorDialog.State |
    never
  );

  export function WantedCalculations(
    page: RoomPage,
    room: Room,
    previousRoom: Room | null,
    calculator: Calculator,
  ): Calculator.WantedList {
    const list: Calculator.WantedList = [];

    const roomStates: Room[] = [room];

    if (previousRoom) {
      roomStates.push(previousRoom);
    }

    function addStateFromPlayers(players: Player.Pair) {
      const bothPlayers = bothOrNull(players);

      if (bothPlayers === null) {
        return;
      }

      for (const outcome of Game.outcomes) {
        if (outcome === 'draw') {
          continue;
        }

        roomStates.push(Room.reduce(room, {
          type: 'add-game',
          data: Game({ players: bothPlayers, outcome }),
        }));
      }
    }

    addStateFromPlayers(page.selectedPlayers);

    const suggestedGames = (Room
      .SuggestedGames(room, calculator)
      .slice(0, 2)
    );

    for (const game of suggestedGames) {
      addStateFromPlayers(game.players);
    }

    for (const roomState of roomStates) {
      const ratingsInput = Room.RatingsInput(roomState);
      const pairValuesInput = Room.PairValuesInput(roomState, calculator);

      if (ratingsInput !== null) {
        list.push({ method: 'Ratings', input: ratingsInput });
      }

      if (pairValuesInput !== null) {
        list.push({ method: 'PairValues', input: pairValuesInput });
      }
    }

    return list;
  }

  export function resolveRoom(page: RoomPage, app: App) {
    return RefSet.resolve(app.entities.rooms, page.room);
  }

  export function needsTimeUpdate(page: RoomPage, app: App): boolean {
    const room = resolveRoom(page, app);

    if (room === null) {
      assert(false);
      return false;
    }

    const ratings = Room.Ratings(room, app.calculator);

    if (ratings === null) {
      return false;
    }

    return (RefMap
      .Refs(ratings)
      .some(ref => {
        const target = RefMap.get(ratings, ref);
        const animated = RefMap.get(page.animatedRatings, ref);

        return target !== animated;
      })
    );
  }

  export function showDialog(
    data: DialogState,
    dispatch: (action: Action) => void
  ) {
    dispatch({ type: 'set-dialog', data });
    setTimeout(() => dispatch({ type: 'show-dialog' }), 10);
  }

  export function hideDialog(dispatch: (action: Action) => void) {
    dispatch({ type: 'hide-dialog' });
    setTimeout(() => dispatch({ type: 'set-dialog' }), 200);
  }
}

export default RoomPage;



// WEBPACK FOOTER //
// ./src/state/RoomPage.ts